/**
 * DeletionScreen — página pública de "eliminación de cuenta y datos".
 *
 * Requerida por Google Play (Data Safety → URL de eliminación de cuenta). Debe:
 *  - nombrar la app/desarrollador,
 *  - mostrar pasos claros para solicitar la eliminación,
 *  - especificar qué datos se borran / se retienen y por cuánto.
 *
 * URL pública: https://aetherlite.nilabs.co/eliminar-cuenta (y /account-deletion).
 * Reusa las clases CSS de la política de privacidad para mantener el estilo.
 */
import { StandaloneHeader } from "./TermsScreen";

interface DeletionScreenProps {
  standalone?: boolean;
}

const SUPPORT_EMAIL = "aetherlite@nilabs.co";

export function DeletionScreen({ standalone }: DeletionScreenProps = {}) {
  return (
    <div className={"al-priv-root " + (standalone ? "al-priv-standalone" : "")}>
      {standalone && <StandaloneHeader title="Eliminación de cuenta y datos" />}

      <article className={standalone ? "al-priv-article-standalone" : "al-priv-article"}>
        {!standalone && <h1 className="al-priv-title">Eliminación de cuenta y datos</h1>}

        <p className="al-priv-lead">
          Esta página explica cómo solicitar la eliminación de tu cuenta y de los
          datos asociados en las apps <strong>AetherLite</strong> (para clientes) y{" "}
          <strong>AetherLite Coach</strong> (para entrenadores), desarrolladas por{" "}
          <strong>Nilabs</strong>.
        </p>

        <h2>Cómo solicitar la eliminación</h2>
        <p>Tenés dos formas:</p>
        <ol>
          <li>
            <strong>Desde la app:</strong>
            <ul>
              <li>
                <strong>Clientes:</strong> tu entrenador puede eliminarte (y borrar todos tus
                datos) desde su app, en el detalle de tu perfil → <em>Eliminar cliente</em>.
                También podés borrar datos puntuales (fotos, medidas) desde la pestaña Progreso.
              </li>
              <li>
                <strong>Entrenadores:</strong> podés eliminar cualquier cliente y sus datos
                desde el detalle del cliente. Para borrar tu cuenta de entrenador completa,
                usá el método por correo.
              </li>
            </ul>
          </li>
          <li>
            <strong>Por correo:</strong> escribí a{" "}
            <a href={`mailto:${SUPPORT_EMAIL}?subject=Eliminar%20mi%20cuenta%20AetherLite`}>
              {SUPPORT_EMAIL}
            </a>{" "}
            <em>desde la dirección de email de tu cuenta</em>, con el asunto
            “Eliminar mi cuenta”. Procesamos la solicitud en un plazo máximo de{" "}
            <strong>30 días</strong> y te confirmamos cuando se complete.
          </li>
        </ol>

        <h2>Qué datos se eliminan</h2>
        <p>Al eliminar la cuenta borramos de forma permanente:</p>
        <ul>
          <li>Tu perfil (nombre, correo, foto, datos de marca del entrenador).</li>
          <li>Tus clientes y sus fichas (en el caso del entrenador).</li>
          <li>Planes, rutinas y dietas creados.</li>
          <li>Fotos de progreso, peso y medidas corporales.</li>
          <li>Sesiones agendadas (citas).</li>
          <li>Tokens de sesión y de notificaciones push.</li>
        </ul>

        <h2>Qué datos se conservan (y por cuánto)</h2>
        <ul>
          <li>
            Registros mínimos de facturación o transacciones que la ley exija conservar,
            durante el período legal aplicable, y de forma disociada cuando es posible.
          </li>
          <li>
            Registros técnicos (logs) anonimizados, sin información que te identifique.
          </li>
        </ul>

        <p className="al-priv-lead" style={{ marginTop: 24 }}>
          Ante cualquier duda sobre tus datos, escribinos a{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Ver también nuestra{" "}
          <a href="/privacy">Política de privacidad</a>.
        </p>
      </article>
    </div>
  );
}
